import { FFmpeg } from '@ffmpeg/ffmpeg';

import { getAppletsUrl } from '@cartwhl/common/utils';

import type { ProgressInfo } from './useFfmpeg';
import { ensureEvenVideoDimensions } from './videoExportDimensions';

const USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME = 'thumbnailCamera';
const DEFAULT_FFMPEG_PROCESSING_SPEED = 'ultrafast';
const FFMPEG_PROCESSING_SPEED_QUERY_PARAMETER = 'ffmpegSpeed';

// Function to generate the MP4 from recorded frame blobs.
export async function createVideoFromBlobs(
  blobs: Blob[],
  outputFilename: string,
  ffmpeg: FFmpeg,
  width: number,
  height: number,
  frameRate: number,
  playbackSpeed: number,
  backgroundColor?: string,
  onProgress?: (progress: ProgressInfo) => void,
  createProgressTracker?: (
    totalFrames: number,
    onProgress?: (progress: ProgressInfo) => void,
  ) => ((event: { message: string }) => void) | undefined,
) {
  if (!ffmpeg) {
    throw new Error('There was a problem initializing the video export.');
  }
  if (!width || !height) {
    throw new Error('Invalid video dimensions.');
  }

  const { width: evenWidth, height: evenHeight } = ensureEvenVideoDimensions(
    width,
    height,
  );

  // Write frame blobs to FFmpeg's virtual file system
  for (let i = 0; i < blobs.length; i++) {
    const data = new Uint8Array(await blobs[i].arrayBuffer());
    await ffmpeg.writeFile(`frame${i}.png`, data);
  }

  onProgress?.({ percentage: 8, time: '00:00.00' });

  // Determine whether or not this is a thumbnail video.
  const queryParams = new URLSearchParams(window.location.search);
  const isThumbnailVideo =
    queryParams.get(USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME) === 'true';
  // Extract processing speed from URL parameters or use default
  const speedFromParams = queryParams.get(
    FFMPEG_PROCESSING_SPEED_QUERY_PARAMETER,
  );

  // List of valid FFmpeg preset speeds
  const validSpeeds = [
    'ultrafast',
    'superfast',
    'veryfast',
    'faster',
    'fast',
    'medium',
    'slow',
    'slower',
    'veryslow',
  ];

  // Validate the speed parameter
  const thumbnailProcessingSpeed =
    speedFromParams && validSpeeds.includes(speedFromParams)
      ? speedFromParams
      : DEFAULT_FFMPEG_PROCESSING_SPEED;

  // Set up progress tracking if callback provided
  let progressHandler: ((event: { message: string }) => void) | undefined;

  if (onProgress && createProgressTracker) {
    const totalFrames = blobs.length;

    progressHandler = createProgressTracker(totalFrames, onProgress);
    if (progressHandler) {
      ffmpeg.on('log', progressHandler);
    }
  }

  if (isThumbnailVideo) {
    await createThumbnailVideoFromBlobs(
      blobs,
      outputFilename,
      ffmpeg,
      evenWidth,
      evenHeight,
      frameRate,
      playbackSpeed,
      thumbnailProcessingSpeed,
    );
  } else {
    await createWatermarkedVideoFromBlobs(
      blobs,
      outputFilename,
      ffmpeg,
      evenWidth,
      evenHeight,
      frameRate,
      playbackSpeed,
      thumbnailProcessingSpeed,
      backgroundColor,
    );
  }

  // Fetch the output video file from FFmpeg's file system
  const data = await ffmpeg.readFile(outputFilename);

  if (typeof data === 'string') {
    throw new Error('Video encode produced an unexpected output format.');
  }

  // Convert the output video to a Blob
  const videoBlob = new Blob([new Uint8Array(data)], { type: 'video/mp4' });

  // Clean up: delete the PNG files from FFmpeg's file system
  for (let i = 0; i < blobs.length; i++) {
    try {
      ffmpeg.deleteFile(`frame${i}.png`);
    } catch (e) {
      console.error('Error deleting file:', `frame${i}.png`, e);
    }
  }

  // Clean up progress handler if it was set up
  if (progressHandler) {
    ffmpeg.off('log', progressHandler);
  }

  return videoBlob;
}

// Function to create MP4 from frames without adding a watermark.
async function createThumbnailVideoFromBlobs(
  blobs: Blob[],
  outputFilename: string,
  ffmpeg: FFmpeg,
  width: number,
  height: number,
  frameRate: number,
  playbackSpeed: number,
  processingSpeed: string,
) {
  if (!ffmpeg) return;
  if (!width || !height) return;

  await ffmpeg.exec([
    '-framerate',
    `${frameRate}`, // Set the frame rate
    '-i',
    'frame%d.png', // Input format for the frames
    '-filter_complex',
    // Scale the video stream to enforce output width and height
    `[0:v]scale=${width}:${height},setpts=PTS/${playbackSpeed}`,
    '-c:v',
    'libx264', // Codec
    '-preset',
    processingSpeed, // Encoding speed
    '-movflags',
    '+faststart',
    '-pix_fmt',
    'yuv420p', // Pixel format
    outputFilename,
  ]);
}

// Function to create MP4 from frames, adding the cartwheel watermark.
async function createWatermarkedVideoFromBlobs(
  blobs: Blob[],
  outputFilename: string,
  ffmpeg: FFmpeg,
  width: number,
  height: number,
  frameRate: number,
  playbackSpeed: number,
  processingSpeed: string,
  backgroundColor?: string,
) {
  if (!ffmpeg) return;
  if (!width || !height) return;

  const urlBase = getAppletsUrl(true);
  const animatedLogo = await fetchImage(`${urlBase}/animatedLogo.gif`);
  const stillLogo = await fetchImage(`${urlBase}/stillLogo.png`);

  // Calculate the logo output resolution
  const logoHeight = Math.min(height / (width < height ? 12 : 9), 64);
  const logoWidth = (animatedLogo.width / animatedLogo.height) * logoHeight;

  // Calculate the logo position based on the output resolution and the output logo resolution
  const gifPosX = width - logoWidth - 40;
  const gifPosY = height - logoHeight - 40;

  // Set the lookup-table (LUT) for the logo color filter
  // We're going to invert the logo colors if the background luminance is < 0.63
  // This is higher than the recommended 0.222 as outlined in https://webaim.org/resources/contrastchecker/
  // We want the logo to remain white for relatively light backgrounds and only switch to black if it becomes completely invisible.
  const backgroundLuminance = getBackgroundLuminance(
    backgroundColor || '#EFEFEF',
  );
  const logoLUT = backgroundLuminance < 0.63 ? 'maxval-val' : 'val';

  // Calculate the createdAt at which the animated logo should start playing
  // We're setting the start time so that the animation ends 10 seconds before the end of the video.
  const videoFrameCount = blobs.length;
  const videoDurationSeconds = videoFrameCount / frameRate / playbackSpeed;
  const animatedLogoFrameCount = 45;
  // gif duration is based on 24fps so needs to be hardcoded
  const animatedLogoDurationSeconds = animatedLogoFrameCount / 24;
  // Short clips can yield negative times → invalid overlay `between()` ranges and failed/wedged encodes.
  const animatedLogoStartTime = Math.max(
    0,
    videoDurationSeconds -
      (animatedLogoDurationSeconds + 10 / frameRate),
  );
  const stillLogoEndTime = Math.max(
    0,
    animatedLogoStartTime - 0.5 / frameRate,
  );

  // Write the animated and still logo blobs to FFmpeg's virtual file system
  ffmpeg.writeFile('animatedLogo.gif', animatedLogo.data);
  ffmpeg.writeFile('stillLogo.png', stillLogo.data);

  // Run FFmpeg command to create a video from the frames
  await ffmpeg.exec([
    '-framerate',
    `${frameRate}`, // Set the frame rate
    '-i',
    'frame%d.png', // Input format for the frames
    '-ignore_loop',
    '1', // Ignore loop for the animated logo
    '-i',
    'animatedLogo.gif', // Input animated logo
    '-i',
    'stillLogo.png', // Input still logo
    '-filter_complex',
    // Scale the video stream to enforce output width and height
    `[0:v]scale=${width}:${height},setpts=PTS/${playbackSpeed}[video_scaled];` +
    // Key out white pixels from animated logo, scale and adjust opacity
    '[1:v]colorlevels=rimax=0:gimax=0:bimax=0[gif_keyed];' +
    `[gif_keyed]scale=${logoWidth}:${logoHeight},lutrgb=r=${logoLUT}:g=${logoLUT}:b=${logoLUT},format=rgba,colorchannelmixer=aa=0.7[gif_transparent];` +
    // Set the presentation createdAt for animated logo
    `[gif_transparent]setpts=PTS-STARTPTS+${animatedLogoStartTime}/TB[gif_ready];` +
    // Key out white pixels from still logo, scale and adjust opacity
    '[2:v]colorlevels=rimax=0:gimax=0:bimax=0[overlay_keyed];' +
    `[overlay_keyed]scale=${logoWidth}:${logoHeight},lutrgb=r=${logoLUT}:g=${logoLUT}:b=${logoLUT},format=rgba,colorchannelmixer=aa=0.7[overlay_ready];` +
    // Overlay the still logo on top of the frames
    `[video_scaled][overlay_ready]overlay=${gifPosX}:${gifPosY}:enable='between(t,0,${stillLogoEndTime})'[video_with_overlay];` +
    // Overlay the animated gif on top of the output
    `[video_with_overlay][gif_ready]overlay=${gifPosX}:${gifPosY}:enable='between(t,${animatedLogoStartTime},inf)'`,
    '-c:v',
    'libx264', // Codec
    '-preset',
    processingSpeed, // Encoding speed
    '-pix_fmt',
    'yuv420p', // Pixel format
    outputFilename,
  ]);
}

// Function to get the background luminance
function getBackgroundLuminance(backgroundColor: string): number {
  // Helper function to get the background color as RGB
  function getBackgroundRGB() {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
      backgroundColor,
    );
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }
  // Helper function to convert RGB component to sRGB
  function sRGB(component: number): number {
    component /= 255; // Scale component to 0-1 range
    return component <= 0.03928
      ? component / 12.92
      : Math.pow((component + 0.055) / 1.055, 2.4);
  }

  // Get the background color as sRGB and calculate its relative luminance
  const rgb = getBackgroundRGB();
  if (!rgb) {
    return 0;
  }
  const luminance =
    0.2126 * sRGB(rgb.r) + 0.7152 * sRGB(rgb.g) + 0.0722 * sRGB(rgb.b);
  return luminance;
}

function parseRasterImageDimensions(data: Uint8Array): {
  width: number;
  height: number;
} {
  if (data.length < 24) {
    throw new Error('Watermark image data too small.');
  }
  // GIF logical screen descriptor
  if (data[0] === 0x47 && data[1] === 0x49 && data[2] === 0x46) {
    return {
      width: data[6]! | (data[7]! << 8),
      height: data[8]! | (data[9]! << 8),
    };
  }
  // PNG IHDR (big-endian width/height at bytes 16–23)
  if (
    data[0] === 0x89 &&
    data[1] === 0x50 &&
    data[2] === 0x4e &&
    data[3] === 0x47
  ) {
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    return {
      width: view.getUint32(16, false),
      height: view.getUint32(20, false),
    };
  }
  throw new Error('Unsupported watermark image format.');
}

/** Fetch bytes and decode intrinsic size (GIF or PNG). */
async function fetchImage(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to load watermark asset (${response.status}): ${url}`,
    );
  }
  const blob = await response.blob();
  const arrayBuffer = await blob.arrayBuffer();
  const data = new Uint8Array(arrayBuffer);
  const { width, height } = parseRasterImageDimensions(data);
  return { data, width, height };
}

export async function createSeekPerformanceOptimizedVideoFromBlob(
  originalBlob: Blob,
  outputFilename: string,
  ffmpeg: FFmpeg,
  onProgress?: (progress: ProgressInfo) => void,
  createProgressTracker?: (
    totalFrames: number,
    onProgress?: (progress: ProgressInfo) => void,
  ) => ((event: { message: string }) => void) | undefined,
  metadata?: {
    duration: number;
    frameRate: number;
    totalFrames: number;
    width: number;
    height: number;
  },
) {
  const inputBuffer = await originalBlob.arrayBuffer();

  // Write the source file with a generic extension
  ffmpeg.writeFile('source_original', new Uint8Array(inputBuffer));

  // Set up progress tracking if callback provided
  let progressHandler: ((event: { message: string }) => void) | undefined;

  if (onProgress && createProgressTracker) {
    // Get accurate frame count from metadata if available
    let estimatedTotalFrames = 1000; // Conservative fallback estimate

    if (metadata) {
      const originalFrameRate = metadata.frameRate;
      const originalTotalFrames = metadata.totalFrames;
      const duration = metadata.duration;

      // Calculate new frame count after conversion to 30fps
      const targetFrameRate = 30;
      const convertedTotalFrames = Math.round(duration * targetFrameRate);

      estimatedTotalFrames = convertedTotalFrames;
    }

    progressHandler = createProgressTracker(estimatedTotalFrames, onProgress);
    if (progressHandler) {
      ffmpeg.on('log', progressHandler);
    }
  }

  // Convert to mp4 with improved settings for all browsers
  await ffmpeg.exec([
    '-i',
    'source_original',
    '-filter:v',
    'fps=fps=30:round=up', // Force 30fps and round up to handle duplicates
    '-c:v',
    'libx264',
    '-preset', // Better performance, worse compression (H.264 only)
    'ultrafast',
    '-b:v',
    '2M',
    '-keyint_min',
    '10',
    '-g',
    '10',
    '-pix_fmt',
    'yuv420p', // Pixel format (8-bit for Safari compatibility)
    '-profile:v',
    'main', // Main profile for H.264 (baseline is too low for modern browsers)
    '-level',
    '4.0',
    '-movflags',
    '+faststart', // Optimize for faster delivery (Safari compatibility)
    '-an',
    outputFilename,
  ]);
  const outputMimeType = 'video/mp4';

  const outputData = await ffmpeg.readFile(outputFilename);
  if (typeof outputData === 'string') throw Error('Invalid output data');
  const blob = new Blob([outputData as BlobPart], { type: outputMimeType });

  // Clean up the original file
  ffmpeg.deleteFile('source_original');

  // Clean up progress handler if it was set up
  if (progressHandler) {
    ffmpeg.off('log', progressHandler);
  }

  return { blob, mimeType: outputMimeType, filename: outputFilename };
}
